// frontend/src/components/devices/DeviceDetails.jsx

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Loading from '../common/Loading';
import SwitchConsumption from '../devices/SwitchConsumption';
import AcControlPanel from './AcControlPanel';

const DeviceDetails = ({ device, onClose, onUpdate }) => {
  const [deviceData, setDeviceData] = useState(null);
  const [switchConfig, setSwitchConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editingConfig, setEditingConfig] = useState(false);
  const [formData, setFormData] = useState({
    name: device.name,
    type: device.type,
  });
  const [configFormData, setConfigFormData] = useState({
    primeFixe: 9955,
    reglageDisjoncteur: '30A',
    puissanceSouscrite: 30,
  });
  const [controlLoading, setControlLoading] = useState(false);

  useEffect(() => {
    fetchDeviceDetails();
    if (device.type === 'switch') {
      fetchSwitchConfig();
    }
  }, []);

  const fetchDeviceDetails = async () => {
    try {
      const response = await api.get(`/devices/${device.deviceId}`);
      setDeviceData(response.data.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSwitchConfig = async () => {
    try {
      const response = await api.get(`/meter-indexes/${device._id}/config`);
      setSwitchConfig(response.data.data);
      setConfigFormData({
        primeFixe: response.data.data.primeFixe,
        reglageDisjoncteur: response.data.data.reglageDisjoncteur,
        puissanceSouscrite: response.data.data.puissanceSouscrite,
      });
    } catch (error) {
      console.error('Erreur config:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/devices/${device.deviceId}`, formData);
      alert('Device mis à jour avec succès');
      setEditing(false);
      onUpdate();
      fetchDeviceDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await api.put(`/meter-indexes/${device._id}/config`, configFormData);
      alert('Configuration de facturation mise à jour avec succès');
      setEditingConfig(false);
      fetchSwitchConfig();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleControl = async (code, value) => {
    setControlLoading(true);
    try {
      await api.post(`/devices/${device.deviceId}/control`, {
        commands: [{ code, value }],
      });
      alert('Commande envoyée avec succès');
      fetchDeviceDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors du contrôle');
    } finally {
      setControlLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        'Êtes-vous sûr de vouloir supprimer ce device de la base de données ?'
      )
    ) {
      try {
        await api.delete(`/devices/${device.deviceId}`);
        alert('Device supprimé avec succès');
        onUpdate();
        onClose();
      } catch (error) {
        alert(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <button
            onClick={onClose}
            className="text-blue-primary hover:text-blue-dark mb-2"
          >
            ← Retour à la liste
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{deviceData.name}</h1>
          <p className="text-gray-600 mt-1">{deviceData.productName}</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setEditing(!editing)}>
            {editing ? 'Annuler' : 'Modifier'}
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <Card className="lg:col-span-2" title="Informations">
          {editing ? (
            <div className="space-y-4">
              <Input
                label="Nom"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                >
                  <option value="sensor">Capteur</option>
                  <option value="switch">Switch</option>
                  <option value="ir_controller">Contrôleur IR</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <Button onClick={handleUpdate} className="w-full">
                Sauvegarder
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <InfoRow label="Device ID" value={deviceData.deviceId} />
              <InfoRow label="Type" value={deviceData.type} />
              <InfoRow
                label="Statut"
                value={
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      deviceData.online
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {deviceData.online ? 'En ligne' : 'Hors ligne'}
                  </span>
                }
              />
              <InfoRow label="Product ID" value={deviceData.productId} />
              <InfoRow label="Catégorie" value={deviceData.category} />
              <InfoRow label="Modèle" value={deviceData.model || 'N/A'} />
              <InfoRow label="IP" value={deviceData.ip || 'N/A'} />
              <InfoRow
                label="Créé le"
                value={
                  deviceData.createTime
                    ? new Date(deviceData.createTime).toLocaleString('fr-FR')
                    : 'N/A'
                }
              />
              <InfoRow
                label="Dernière sync"
                value={new Date(deviceData.lastSynced).toLocaleString('fr-FR')}
              />
            </div>
          )}
        </Card>

        {/* Statut en temps réel */}
        {deviceData.type === 'ir_controller' ? (
          <Card title="Statut en temps réel">
            {deviceData.linkedAssociation?.hasRemoteId ? (
              <AcControlPanel
                associationId={deviceData.linkedAssociation._id}
                associationName={deviceData.linkedAssociation.name}
              />
            ) : deviceData.linkedAssociation ? (
              <p className="text-gray-500 text-center py-4">
                🌡️ Association "{deviceData.linkedAssociation.name}" trouvée, mais aucun remoteId
                n'est configuré dessus. Modifiez l'association pour renseigner le remoteId de la
                climatisation.
              </p>
            ) : (
              <p className="text-gray-500 text-center py-4">
                🌡️ Ce Contrôleur IR n'est associé à aucune télécommande pour le moment.
                Créez une association (Sensor ↔ Contrôleur IR) avec le remoteId de la climatisation
                pour pouvoir la piloter ici.
              </p>
            )}
          </Card>
        ) : (
          <Card title="Statut en temps réel">
            {deviceData.realtimeStatus && deviceData.realtimeStatus.length > 0 ? (
              <div className="space-y-3">
                {deviceData.realtimeStatus.map((status, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">
                        {status.code}
                      </span>
                      <span className="text-sm text-gray-900 font-semibold">
                        {typeof status.value === 'boolean'
                          ? status.value
                            ? 'ON'
                            : 'OFF'
                          : status.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                Aucune donnée de statut disponible
              </p>
            )}
          </Card>
        )}
      </div>

      {/* Configuration de facturation (si switch) */}
      {deviceData.type === 'switch' && switchConfig && (
        <Card title="⚙️ Paramètres de facturation" className="mt-6">
          {editingConfig ? (
            <div className="space-y-4">
              <Input
                label="Prime fixe (FCFA)"
                type="number"
                value={configFormData.primeFixe}
                onChange={(e) =>
                  setConfigFormData({
                    ...configFormData,
                    primeFixe: parseFloat(e.target.value),
                  })
                }
              />
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Réglage disjoncteur
                </label>
                <select
                  value={configFormData.reglageDisjoncteur}
                  onChange={(e) =>
                    setConfigFormData({
                      ...configFormData,
                      reglageDisjoncteur: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
                >
                  <option value="15A">15A</option>
                  <option value="30A">30A</option>
                  <option value="60A">60A</option>
                </select>
              </div>
              <Input
                label="Puissance souscrite (Ampères)"
                type="number"
                value={configFormData.puissanceSouscrite}
                onChange={(e) =>
                  setConfigFormData({
                    ...configFormData,
                    puissanceSouscrite: parseFloat(e.target.value),
                  })
                }
              />
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setEditingConfig(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button onClick={handleUpdateConfig} className="flex-1">
                  Sauvegarder
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <InfoRow
                label="Prime fixe"
                value={`${switchConfig.primeFixe.toLocaleString()} FCFA`}
              />
              <InfoRow
                label="Réglage disjoncteur"
                value={switchConfig.reglageDisjoncteur}
              />
              <InfoRow
                label="Puissance souscrite"
                value={`${switchConfig.puissanceSouscrite} A`}
              />
              <Button
                variant="secondary"
                onClick={() => setEditingConfig(true)}
                className="w-full"
              >
                Modifier les paramètres
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Contrôles (si switch) */}
      {deviceData.type === 'switch' && (
        <Card title="Contrôles" className="mt-6">
          <div className="flex space-x-4">
            <Button
              onClick={() => handleControl('switch', true)}
              disabled={controlLoading}
              variant="success"
              className="flex-1"
            >
              Allumer
            </Button>
            <Button
              onClick={() => handleControl('switch', false)}
              disabled={controlLoading}
              variant="danger"
              className="flex-1"
            >
              Éteindre
            </Button>
          </div>
        </Card>
      )}

      {/* Dashboard de consommation (si switch) */}
      {deviceData.type === 'switch' && (
        <div className="mt-6">
          <SwitchConsumption switchId={device._id} />
        </div>
      )}

      {/* Détails techniques */}
      {deviceData.realtimeDetails && (
        <Card title="Détails techniques" className="mt-6">
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-xs">
            {JSON.stringify(deviceData.realtimeDetails, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
};

// Composant helper pour afficher une ligne d'info
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="text-gray-600 font-medium">{label}:</span>
    <span className="text-gray-900">{value}</span>
  </div>
);

export default DeviceDetails;